import React, { useState } from "react";
import "./MealInDayCard.css";

/**
 * MealInDayCard Component
 *
 * Displays a single daily meal plan with all meals (breakfast to supper).
 * Expandable to show detailed meal information and macros.
 */
const MealInDayCard = ({ mealInDay, onEdit, onDelete, onCreateShoppingList }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const looseProducts = mealInDay.looseProducts || [];

  /**
   * Calculate total macros for all meals in the day
   */
  // Calculate total macros from all meals and loose products
  const getTotalMacros = () => {
    const meals = [
      { meal: mealInDay.breakfast, factor: mealInDay.factorBreakfast || 1.0 },
      { meal: mealInDay.secondBreakfast, factor: mealInDay.factorSecondBreakfast || 1.0 },
      { meal: mealInDay.lunch, factor: mealInDay.factorLunch || 1.0 },
      { meal: mealInDay.afternoonSnack, factor: mealInDay.factorAfternoonSnack || 1.0 },
      { meal: mealInDay.dinner, factor: mealInDay.factorDinner || 1.0 },
      { meal: mealInDay.supper, factor: mealInDay.factorSupper || 1.0 },
    ];

    let totals = { calories: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0 };

    // Add macros from meals
    meals.forEach(({ meal, factor }) => {
      if (meal?.productsInMeal && Array.isArray(meal.productsInMeal)) {
        meal.productsInMeal.forEach((item) => {
          const weight = item.weight || 100;
          const product = item.product || {};

          const kcal = product.kcalPer100 || 0;
          const proteins = product.proteins || 0;
          const carbs = product.sugarAndCarb || ((product.sugar || 0) + (product.carbohydrates || 0));
          const fats = product.fat || 0;
          const fiber = product.fiber || 0;

          totals.calories += (kcal * weight * factor) / 100;
          totals.proteins += (proteins * weight * factor) / 100;
          totals.carbs += (carbs * weight * factor) / 100;
          totals.fats += (fats * weight * factor) / 100;
          totals.fiber += (fiber * weight * factor) / 100;
        });
      }
    });

    // Add macros from loose products
    looseProducts.forEach(({ product, weight }) => {
      const kcal = product.kcalPer100 || 0;
      const proteins = product.proteins || 0;
      const carbs = product.sugarAndCarb || ((product.sugar || 0) + (product.carbohydrates || 0));
      const fats = product.fat || 0;
      const fiber = product.fiber || 0;

      totals.calories += (kcal * weight) / 100;
      totals.proteins += (proteins * weight) / 100;
      totals.carbs += (carbs * weight) / 100;
      totals.fats += (fats * weight) / 100;
      totals.fiber += (fiber * weight) / 100;
    });

    return totals;
  };

  const totalMacros = getTotalMacros();

  // Safety check for undefined values
  const safeMacros = {
    calories: totalMacros?.calories || 0,
    proteins: totalMacros?.proteins || 0,
    carbs: totalMacros?.carbs || 0,
    fats: totalMacros?.fats || 0,
    fiber: totalMacros?.fiber || 0,
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper function to render a meal
  const renderMeal = (label, meal, factorRaw) => {
    if (!meal) return null;
    const factor = factorRaw || 1.0;

    // Calculate meal macros
    let mealMacros = { calories: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0 };
    if (meal.productsInMeal && meal.productsInMeal.length > 0) {
      meal.productsInMeal.forEach((item) => {
        const weight = item.weight || 100;
        const product = item.product || {};

        const kcal = product.kcalPer100 || 0;
        const proteins = product.proteins || 0;
        const carbs = product.sugarAndCarb || ((product.sugar || 0) + (product.carbohydrates || 0));
        const fats = product.fat || 0;
        const fiber = product.fiber || 0;

        mealMacros.calories += (kcal * weight * factor) / 100;
        mealMacros.proteins += (proteins * weight * factor) / 100;
        mealMacros.carbs += (carbs * weight * factor) / 100;
        mealMacros.fats += (fats * weight * factor) / 100;
        mealMacros.fiber += (fiber * weight * factor) / 100;
      });
    }

    return (
      <div className="meal-in-day-card__meal">
        <div className="meal-in-day-card__meal-header">
          <div className="meal-in-day-card__meal-title">
            <span className="meal-in-day-card__meal-label">{label}</span>
            <span className="meal-in-day-card__meal-name">{meal.name}</span>
            {factor !== 1.0 && <span className="meal-in-day-card__meal-factor">×{factor}</span>}
          </div>
          <div className="meal-in-day-card__meal-macros-summary">
            <span className="meal-in-day-card__meal-macro">🔥 {mealMacros.calories.toFixed(0)} kcal</span>
            <span className="meal-in-day-card__meal-macro">💪 {mealMacros.proteins.toFixed(1)}g</span>
            <span className="meal-in-day-card__meal-macro">🍞 {mealMacros.carbs.toFixed(1)}g</span>
            <span className="meal-in-day-card__meal-macro">🥑 {mealMacros.fats.toFixed(1)}g</span>
            {mealMacros.fiber > 0 && <span className="meal-in-day-card__meal-macro">🌾 {mealMacros.fiber.toFixed(1)}g</span>}
          </div>
        </div>
        {meal.productsInMeal && meal.productsInMeal.length > 0 && (
          <div className="meal-in-day-card__products">
            {meal.productsInMeal.map((item) => {
              const product = item.product || {};
              const weight = item.weight || 0;
              const totalWeight = weight * factor;
              const unitWeight = product.weight || 0;
              
              let quantityText = "";
              if (unitWeight > 0) {
                const qty = totalWeight / unitWeight;
                const formattedQty = parseFloat(qty.toFixed(2));
                quantityText = ` (${formattedQty} szt.)`;
              }

              const kcal = ((product.kcalPer100 || 0) * totalWeight) / 100;
              const proteins = ((product.proteins || 0) * totalWeight) / 100;
              const carbs = ((product.sugarAndCarb || ((product.sugar || 0) + (product.carbohydrates || 0))) * totalWeight) / 100;
              const fats = ((product.fat || 0) * totalWeight) / 100;
              const fiber = ((product.fiber || 0) * totalWeight) / 100;

              return (
                <div key={item.id} className="meal-in-day-card__product-container">
                  <div className="meal-in-day-card__product">
                    <span className="meal-in-day-card__product-name">{product.name}</span>
                    <span className="meal-in-day-card__product-weight">{totalWeight.toFixed(0)}g{quantityText}</span>
                  </div>
                  <div className="meal-in-day-card__product-macros">
                    <span className="meal-in-day-card__product-macro">🔥 {kcal.toFixed(0)} kcal</span>
                    <span className="meal-in-day-card__product-macro">💪 {proteins.toFixed(1)}g</span>
                    <span className="meal-in-day-card__product-macro">🍞 {carbs.toFixed(1)}g</span>
                    <span className="meal-in-day-card__product-macro">🥑 {fats.toFixed(1)}g</span>
                    {fiber > 0 && <span className="meal-in-day-card__product-macro">🌾 {fiber.toFixed(1)}g</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {meal.recipe && (
          <div className="meal-in-day-card__recipe">
            <h5 className="meal-in-day-card__recipe-title">Przepis:</h5>
            <p className="meal-in-day-card__recipe-text">{meal.recipe}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`meal-in-day-card ${isExpanded ? "meal-in-day-card--expanded" : ""}`}>
      {/* Collapsed View */}
      <div className="meal-in-day-card__header" onClick={toggleExpand}>
        <div className="meal-in-day-card__title">
          <h3>{mealInDay.name}</h3>
          {mealInDay.for5Days && <span className="meal-in-day-card__badge">📅 5 dni</span>}
        </div>
        <div className="meal-in-day-card__summary">
          <div className="meal-in-day-card__macro-compact">
            <span className="meal-in-day-card__macro-label">Kcal</span>
            <span className="meal-in-day-card__macro-value">{safeMacros.calories.toFixed(0)}</span>
          </div>
          <div className="meal-in-day-card__macro-compact">
            <span className="meal-in-day-card__macro-label">Białko</span>
            <span className="meal-in-day-card__macro-value">{safeMacros.proteins.toFixed(1)}g</span>
          </div>
          <div className="meal-in-day-card__macro-compact">
            <span className="meal-in-day-card__macro-label">Węgle</span>
            <span className="meal-in-day-card__macro-value">{safeMacros.carbs.toFixed(1)}g</span>
          </div>
          <div className="meal-in-day-card__macro-compact">
            <span className="meal-in-day-card__macro-label">Tłuszcze</span>
            <span className="meal-in-day-card__macro-value">{safeMacros.fats.toFixed(1)}g</span>
          </div>
          <div className="meal-in-day-card__macro-compact">
            <span className="meal-in-day-card__macro-label">Błonnik</span>
            <span className="meal-in-day-card__macro-value">{safeMacros.fiber.toFixed(1)}g</span>
          </div>

          <div className="meal-in-day-card__header-actions" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onCreateShoppingList(mealInDay)}
              className="meal-in-day-card__button meal-in-day-card__button--create-list"
              title="Stwórz listę zakupów"
            >
              🛒
            </button>
            <button
              onClick={() => onEdit(mealInDay)}
              className="meal-in-day-card__button meal-in-day-card__button--edit"
              title="Edytuj"
            >
              ✏️
            </button>
            <button
              onClick={() => onDelete(mealInDay.id)}
              className="meal-in-day-card__button meal-in-day-card__button--delete"
              title="Usuń"
            >
              🗑️
            </button>
          </div>

          <div className="meal-in-day-card__expand-icon">{isExpanded ? "▲" : "▼"}</div>
        </div>
      </div>

      {/* Expanded View */}
      {isExpanded && (
        <div className="meal-in-day-card__content">
          {/* Meals List */}
          <div className="meal-in-day-card__meals">
            <h4>Posiłki:</h4>
            {renderMeal("🌅 Śniadanie", mealInDay.breakfast, mealInDay.factorBreakfast)}
            {renderMeal("🥐 Drugie śniadanie", mealInDay.secondBreakfast, mealInDay.factorSecondBreakfast)}
            {!mealInDay.for5Days && renderMeal("🍽️ Lunch", mealInDay.lunch, mealInDay.factorLunch)}
            {mealInDay.for5Days && renderMeal("🥘 Obiad", mealInDay.dinner, mealInDay.factorDinner)}
            {mealInDay.for5Days && renderMeal("☕ Podwieczorek", mealInDay.afternoonSnack, mealInDay.factorAfternoonSnack)}
            {renderMeal("🌙 Kolacja", mealInDay.supper, mealInDay.factorSupper)}
          </div>

          {/* Loose Products */}
          {looseProducts.length > 0 && (
            <div className="meal-in-day-card__loose-products">
              <h4>Luźne produkty:</h4>
              {looseProducts.map((lp) => {
                const product = lp.product;
                const weight = lp.weight;
                const unitWeight = product.weight || 0;
                let quantityText = "";
                if (unitWeight > 0) {
                  const qty = weight / unitWeight;
                  const formattedQty = parseFloat(qty.toFixed(2));
                  quantityText = ` (${formattedQty} szt.)`;
                }
                const kcal = ((product.kcalPer100 || 0) * weight) / 100;
                const proteins = ((product.proteins || 0) * weight) / 100;
                const carbs = ((product.sugarAndCarb || ((product.sugar || 0) + (product.carbohydrates || 0))) * weight) / 100;
                const fats = ((product.fat || 0) * weight) / 100;
                const fiber = ((product.fiber || 0) * weight) / 100;

                return (
                  <div key={lp.id} className="meal-in-day-card__loose-product">
                    <div className="meal-in-day-card__loose-product-header">
                      <span className="meal-in-day-card__loose-product-name">{product.name}</span>
                      <span className="meal-in-day-card__loose-product-weight">{weight}g{quantityText}</span>
                    </div>
                    <div className="meal-in-day-card__loose-product-macros">
                      <span className="meal-in-day-card__loose-product-macro">🔥 {kcal.toFixed(0)} kcal</span>
                      <span className="meal-in-day-card__loose-product-macro">💪 {proteins.toFixed(1)}g</span>
                      <span className="meal-in-day-card__loose-product-macro">🍞 {carbs.toFixed(1)}g</span>
                      <span className="meal-in-day-card__loose-product-macro">🥑 {fats.toFixed(1)}g</span>
                      {fiber > 0 && <span className="meal-in-day-card__loose-product-macro">🌾 {fiber.toFixed(1)}g</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Total Macros */}
          <div className="meal-in-day-card__total-macros">
            <h4>Łącznie:</h4>
            <div className="meal-in-day-card__details-grid">
              <div className="meal-in-day-card__detail-item">
                <span className="meal-in-day-card__detail-icon">🔥</span>
                <span className="meal-in-day-card__detail-label">Kalorie (całość)</span>
                <span className="meal-in-day-card__detail-value">{safeMacros.calories.toFixed(0)} kcal</span>
              </div>
              <div className="meal-in-day-card__detail-item">
                <span className="meal-in-day-card__detail-icon">💪</span>
                <span className="meal-in-day-card__detail-label">Białko</span>
                <span className="meal-in-day-card__detail-value">{safeMacros.proteins.toFixed(1)}g</span>
              </div>
              <div className="meal-in-day-card__detail-item">
                <span className="meal-in-day-card__detail-icon">🍞</span>
                <span className="meal-in-day-card__detail-label">Węglowodany</span>
                <span className="meal-in-day-card__detail-value">{safeMacros.carbs.toFixed(1)}g</span>
              </div>
              <div className="meal-in-day-card__detail-item">
                <span className="meal-in-day-card__detail-icon">🥑</span>
                <span className="meal-in-day-card__detail-label">Tłuszcze</span>
                <span className="meal-in-day-card__detail-value">{safeMacros.fats.toFixed(1)}g</span>
              </div>
              {safeMacros.fiber > 0 && (
                <div className="meal-in-day-card__detail-item">
                  <span className="meal-in-day-card__detail-icon">🌾</span>
                  <span className="meal-in-day-card__detail-label">Błonnik</span>
                  <span className="meal-in-day-card__detail-value">{safeMacros.fiber.toFixed(1)}g</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealInDayCard;
